import React, { useState } from "react";
import moment from "moment";

import {
  FAILED_SSN_SAVE_ERROR_MESSAGE,
  GENERIC_ERROR_MESSAGE,
  GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY,
  INVALID_AGE_ERROR_MESSAGE,
  INVALID_DOB_ERROR_MESSAGE,
  INVALID_SSN_ERROR_MESSAGE,
  RE_ENTER_PROMPT,
  VALID_AGE,
  VALID_SSN_LENGTH,
} from "../../../constants";

import { GET_APPLICATION_INFO } from "../../../api/gql";
import { CHECK_USER_IDENTITY } from "./gql";

/* Actions */
import { usePromisedLazyQuery } from "../../../state/store/customHooks";

import { getAge } from "../../../utilities/userInfo";
import isKey from "../../../utilities/keyboard-events";

import { ReadOnly , DatePicker } from "../../common";
import SSNInput from "../../common/SSNInput";
import Sentry from "../../../api/sentry";
import withQuery from "../wrappers/gql/withQuery";
import ContinueButton from "../common/ContinueButton";

const VerifyIdentityForm = ({ readOnly, _completeStep, data: applicationInfo }) => {

  const initializedDob = applicationInfo?.getApplicationInfo?.dob;

  const [dob, setDob] = useState(initializedDob);
  const [momentDob, setMomentDob] = useState(null);
  const [errorDob, setErrorDob] = useState(null);

  const [ssn, setSsn] = useState(null);
  const [retrySsnEntry, setRetrySsnEntry] = useState(false);
  const [errorSsn, setErrorSsn] = useState("");

  const [errorGql, setErrorGql] = useState(null);

  const [ checkValidIdentity, { loading: checkValidIdentityLoading }]
    = usePromisedLazyQuery(CHECK_USER_IDENTITY);

  // helper functions
  const onDateChange = (date) => {
    if(!date){
      setErrorDob("Please enter a valid date");
      return;
    }
    setErrorDob(null);
    const momentDate = moment(date);
    const formattedDob = momentDate.format("YYYYMMDD");
    setMomentDob(momentDate);
    setDob(formattedDob);
  };

  const isFormValid = () => {
    if(!ssn || ssn.length < VALID_SSN_LENGTH) {
      setErrorSsn(INVALID_SSN_ERROR_MESSAGE);
      return false;
    }
    setErrorSsn(null);

    if(!dob) {
      setErrorDob(INVALID_DOB_ERROR_MESSAGE);
      return false;
    }

    const formattedDob = momentDob.format("YYYY/MM/DD");
    if(getAge(formattedDob) < VALID_AGE) {
      setErrorDob(INVALID_AGE_ERROR_MESSAGE);
      return false;
    }

    return true;
  };

  const onFormSubmission = async () => {
    if (readOnly) {
      _completeStep();
      return;
    }

    if(!isFormValid())  return;

    const formattedDob = momentDob.format("YYYY-MM-DD");
    let data;
    try {
      data = await checkValidIdentity({
        variables: {
          userSSN: ssn,
          hasUserResumbitted: retrySsnEntry,
          userDOB: formattedDob,
        },
      });
    } catch (err) {
      Sentry.captureException(err);
      setErrorGql(GENERIC_ERROR_MESSAGE);
      return;
    }

    const { checkUserValidInformation } = data;
    const { shouldReenterInformation, error: verificationError } = checkUserValidInformation;

    if(verificationError){
      setErrorGql(FAILED_SSN_SAVE_ERROR_MESSAGE);
      return;
    }

    if(shouldReenterInformation && !retrySsnEntry){
      setErrorGql(RE_ENTER_PROMPT);
      setRetrySsnEntry(true);
      return;
    }

    _completeStep(ssn, formattedDob);
  };

  return (
    <div onKeyDown={(e) => {
      if(isKey(e, ["enter"]))  onFormSubmission();
    }}>
      <ReadOnly readOnly={readOnly} >
        <div className={`inputDescriptionText ${errorDob ? "errorText" : ""}`}>
          {errorDob || "Date of Birth"}
        </div>
        <DatePicker
          defaultDate={dob}
          dateChange={onDateChange}
          selectBoxClassName={`inputField bg-white date-box ${errorDob ? "redBorder" : ""}`
          }
          disabled={readOnly}
        />
        <div className={`inputDescriptionText mt-1 mb-1 ${errorSsn ? "errorText" : ""}`}>
          { errorSsn || "Social Security Number" }
        </div>
        <SSNInput
          onChange={(ssnValue) => {
            setErrorSsn(null);
            setSsn(ssnValue);
          }}
          placeholder="XXX-XX-XXXX"
          className={`inputField inputFieldText mb-0 ${ errorSsn ? "redBorder" : ""}` }
          style={{ letterSpacing: "0.5ch" }}
          disabled={readOnly}
        />
      </ReadOnly>
      { errorSsn && <SSNSupport /> }
      <ContinueButton onClick={onFormSubmission} loading={checkValidIdentityLoading} />
      { errorGql && <div className="errorText">{errorGql}</div>}
    </div>
  );
};

const SSNSupport = () => (
  <span className="ml-2 text-gray-500 text-sm">
    <i className="far fa-question-circle" />
    <span className="text-sm ml-1">
      Trouble with SSN? Please <a href={`${process.env.REACT_APP_DOMAIN}/support`} rel="noreferrer" target="_blank" className="text-green-primary">contact us.</a>
    </span>
  </span>
);

export default withQuery(GET_APPLICATION_INFO, GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY)(VerifyIdentityForm);